import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, ClipboardList, Eye, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const InventoryDashboard = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/inventory`);
      setRecords(res.data);
    } catch (error) {
      toast.error('Failed to load inventory records');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inventory record?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/inventory/${id}`);
      toast.success('Record deleted');
      fetchRecords();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-SG', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="min-h-screen pt-44 pb-12 px-4 bg-gray-50">
      <div className="container mx-auto max-w-6xl">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3" data-testid="inventory-dashboard-title">
              <ClipboardList className="text-blue-600" /> Warehouse Inventory
            </h1>
            <p className="text-gray-600">Monthly stock take records</p>
          </div>
          <Button data-testid="create-inventory-btn" onClick={() => navigate('/admin/inventory/create')}>
            <Plus size={16} className="mr-2" /> New Stock Take
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12"><p>Loading...</p></div>
        ) : records.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <ClipboardList size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No inventory records yet</p>
              <p className="text-gray-400 mb-4">Create your first stock take record</p>
              <Button onClick={() => navigate('/admin/inventory/create')}>
                <Plus size={16} className="mr-2" /> New Stock Take
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100 border-b">
                    <th className="p-3 text-left">Inventory No</th>
                    <th className="p-3 text-left">Customer / Company</th>
                    <th className="p-3 text-center">Date</th>
                    <th className="p-3 text-center">Items</th>
                    <th className="p-3 text-center">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(record => (
                    <tr key={record.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-semibold text-blue-600">{record.inventory_number}</td>
                      <td className="p-3">
                        <p className="font-medium">{record.company_name || record.customer_name}</p>
                        {record.person_in_charge && <p className="text-sm text-gray-500">PIC: {record.person_in_charge}</p>}
                      </td>
                      <td className="p-3 text-center">{formatDate(record.inventory_date)}</td>
                      <td className="p-3 text-center">{record.items?.length || 0}</td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          record.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {record.status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex gap-2 justify-end">
                          <Button variant="outline" size="sm" onClick={() => navigate(`/admin/inventory/${record.id}`)}>
                            <Eye size={14} className="mr-1" /> View
                          </Button>
                          <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDelete(record.id)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default InventoryDashboard;
